import { memo, useState } from 'react';
import { useService } from './RawRegulations.hooks.ts';
import type { RawRegulationsFormValues } from './RawRegulations.types.ts';

import RawRegulations from './RawRegulations';
import { cloneDeep } from 'lodash';
import { getRawRegulationData } from './RawRegulations.helpers.ts';
import { exportRawRegulationsCsv } from './RawRegulations.services.ts';

export const RawRegulationsContainer = () => {
  const [openRawRegulationDrawer, setOpenRawRegulationDrawer] = useState<boolean>(false);
  const [regulationId, setRegulationId] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const { updateMutation, regulations, isRegulationsFetching } = useService({
    regulationId,
    pageNumber,
    pageSize,
    searchTerm,
  });

  const handleOpenRawRegulationDrawer = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    id: number | null,
  ) => {
    if (event) event.preventDefault();
    if (id === undefined) return;

    setRegulationId(id);
    setOpenRawRegulationDrawer(true);
  };

  const handleCloseRawRegulationDrawer = () => {
    setRegulationId(null);
    setOpenRawRegulationDrawer(false);
  };

  const handleSubmitRawRegulation = (_values: RawRegulationsFormValues) => {
    const values = cloneDeep(_values);

    const data = getRawRegulationData(values);

    if (values?.id) {
      updateMutation.mutate({ data: { ...data, activate: true } });
      handleCloseRawRegulationDrawer();
      return;
    }
    handleCloseRawRegulationDrawer();
  };

  const handleExportRawRegulations = async () => {
    const response = await exportRawRegulationsCsv();

    if (!response?.data) return;

    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const contentDisposition = response.headers['content-disposition'];
    const encodedMatch = contentDisposition?.match(/filename_?\*?=UTF-8''([^;]+)/i)?.[1];
    const fileName = encodedMatch && decodeURIComponent(encodedMatch.trim());
    
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <RawRegulations
      rawRegulations={regulations}
      openRawRegulationDrawer={openRawRegulationDrawer}
      onOpenRawRegulationDrawer={handleOpenRawRegulationDrawer}
      onCloseRawRegulationDrawer={handleCloseRawRegulationDrawer}
      onSubmitRawRegulation={handleSubmitRawRegulation}
      regulationId={regulationId}
      pageNumber={pageNumber}
      pageSize={pageSize}
      setPageNumber={setPageNumber}
      setPageSize={setPageSize}
      searchInput={searchInput}
      onChangeSearchInput={setSearchInput}
      onSearchRawRegulations={(value) => {
        setPageNumber(1);
        setSearchTerm(value.trim());
      }}
      onExportRawRegulations={handleExportRawRegulations}
      isRegulationsFetching={isRegulationsFetching}
    />
  );
};

export default memo(RawRegulationsContainer);
